import { useCallback, useRef, useState } from 'react';

const draggingFiles = (event: React.DragEvent) =>
  event.dataTransfer.types.includes('Files');

type Options = {
  disabled: boolean;
  accept: readonly string[];
  onFiles: (files: File[]) => void;
  onRejected?: (files: File[]) => void;
};

// Tracks an active file drag over a drop target and forwards accepted files.
// dragDepth counts enter/leave across nested children so the overlay does not
// flicker when the pointer moves between them.
export const useFileDropzone = ({
  disabled,
  accept,
  onFiles,
  onRejected,
}: Options) => {
  const [isDragging, setIsDragging] = useState(false);
  const dragDepth = useRef(0);

  const onDragEnter = useCallback(
    (event: React.DragEvent) => {
      if (disabled || !draggingFiles(event)) return;
      event.preventDefault();
      dragDepth.current += 1;
      setIsDragging(true);
    },
    [disabled],
  );

  const onDragOver = useCallback((event: React.DragEvent) => {
    if (!draggingFiles(event)) return;
    event.preventDefault();
  }, []);

  const onDragLeave = useCallback((event: React.DragEvent) => {
    if (!draggingFiles(event)) return;
    dragDepth.current -= 1;
    if (dragDepth.current <= 0) {
      dragDepth.current = 0;
      setIsDragging(false);
    }
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      dragDepth.current = 0;
      setIsDragging(false);
      if (disabled) return;
      const dropped = Array.from(event.dataTransfer.files);
      const accepted = dropped.filter((file) =>
        accept.some((type) => type === file.type),
      );
      const rejected = dropped.filter((file) => !accepted.includes(file));
      if (accepted.length > 0) onFiles(accepted);
      if (rejected.length > 0) onRejected?.(rejected);
    },
    [disabled, accept, onFiles, onRejected],
  );

  return {
    isDragging,
    dropzoneProps: { onDragEnter, onDragOver, onDragLeave, onDrop },
  };
};
